import React, { useState, useEffect, useRef } from 'react';
import './PlatformSettingsManager.css';
import { platformSettingsAPI } from '../../../../services/api/platformSettingsAPI';
import { adminSettingsAPI } from '../../../../services/api/adminSettingsAPI';
import { adminHasPermission } from '../../../../services/auth/authManager';

const DEFAULT_SETTINGS = {
  platformName: 'StandAlone Exchange',
  platformIconDataUrl: '',
  platformDescription: 'A powerful trading platform for digital assets and forex.',
  supportEmail: 'support@example.com',
  supportUrl: '',
  termsUrl: '',
  privacyUrl: '',
  orderIdPrefix: '',
  socialLinks: {
    twitter: '',
    discord: '',
    telegram: '',
    medium: '',
    github: ''
  }
};

const PlatformSettingsManager = () => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [formData, setFormData] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [removeIcon, setRemoveIcon] = useState(false);
  const fileInputRef = useRef(null);

  const [userIdPrefix, setUserIdPrefix] = useState('');
  const [userIdPrefixInput, setUserIdPrefixInput] = useState('');
  const [userIdPrefixLoading, setUserIdPrefixLoading] = useState(false);
  const [userIdPrefixSaving, setUserIdPrefixSaving] = useState(false);
  const [userIdPrefixError, setUserIdPrefixError] = useState(null);
  const [userIdPrefixSuccess, setUserIdPrefixSuccess] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    fetchUserIdPrefix();
  }, []);

  const fetchUserIdPrefix = async () => {
    try {
      setUserIdPrefixLoading(true);
      setUserIdPrefixError(null);
      const response = await adminSettingsAPI.getUserIdPrefix();
      const data = response.data.data || response.data;
      const prefix = data.userIdPrefix || '';
      setUserIdPrefix(prefix);
      setUserIdPrefixInput(prefix);
    } catch (err) {
      setUserIdPrefixError('Failed to fetch account ID prefix: ' + (err.message || 'Unknown error'));
    } finally {
      setUserIdPrefixLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl !== settings.platformIconDataUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl, settings.platformIconDataUrl]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await platformSettingsAPI.get();

      let data = response.data;
      if (data && data.data) {
        data = data.data;
      }

      if (data && typeof data === 'object') {
        const merged = {
          ...DEFAULT_SETTINGS,
          ...data,
          socialLinks: {
            ...DEFAULT_SETTINGS.socialLinks,
            ...(data.socialLinks || {})
          }
        };
        setSettings(merged);
        setFormData(merged);
        if (merged.platformIconDataUrl) {
          setPreviewUrl(merged.platformIconDataUrl);
        }
        setRemoveIcon(false);
      }
    } catch (err) {
      setError('Failed to fetch platform settings: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSocialLinkChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      socialLinks: {
        ...prev.socialLinks,
        [name]: value
      }
    }));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        if (img.width > 256 || img.height > 256) {
          setError(`Icon dimensions must be at most 256x256. Selected image is ${img.width}x${img.height}.`);
          if (fileInputRef.current) fileInputRef.current.value = '';
          return;
        }
        setError(null);
        setSelectedFile(file);
        setRemoveIcon(false);
        setPreviewUrl(ev.target.result);
      };
      img.onerror = () => {
        setError('Failed to read image file. Please select a valid image.');
        if (fileInputRef.current) fileInputRef.current.value = '';
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveIcon = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setRemoveIcon(true);
    setFormData(prev => ({ ...prev, platformIconDataUrl: '' }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      const fd = new FormData();
      fd.append('platformName', formData.platformName.trim());
      fd.append('platformDescription', formData.platformDescription.trim());
      fd.append('supportEmail', formData.supportEmail.trim());
      fd.append('supportUrl', formData.supportUrl.trim());
      fd.append('termsUrl', formData.termsUrl.trim());
      fd.append('privacyUrl', formData.privacyUrl.trim());
      fd.append('orderIdPrefix', formData.orderIdPrefix.trim());
      fd.append('socialLinks', JSON.stringify({
        twitter: formData.socialLinks.twitter.trim(),
        discord: formData.socialLinks.discord.trim(),
        telegram: formData.socialLinks.telegram.trim(),
        medium: formData.socialLinks.medium.trim(),
        github: formData.socialLinks.github.trim()
      }));

      if (removeIcon) {
        fd.append('removeIcon', 'true');
      } else if (selectedFile) {
        fd.append('platformIcon', selectedFile);
      }

      const response = await platformSettingsAPI.update(fd);

      let data = response.data;
      if (data && data.data) {
        data = data.data;
      }

      let iconDataUrl = '';

      if (data && typeof data === 'object') {
        const merged = {
          ...DEFAULT_SETTINGS,
          ...data,
          socialLinks: {
            ...DEFAULT_SETTINGS.socialLinks,
            ...(data.socialLinks || {})
          }
        };
        setSettings(merged);
        setFormData(merged);
        setSelectedFile(null);
        setRemoveIcon(false);
        iconDataUrl = merged.platformIconDataUrl || '';
        if (merged.platformIconDataUrl) {
          setPreviewUrl(merged.platformIconDataUrl);
        } else {
          setPreviewUrl(null);
        }
      }

      setSuccessMessage('Platform settings updated successfully.');
      setTimeout(() => setSuccessMessage(null), 3000);

      window.dispatchEvent(new CustomEvent('platform-settings-updated', {
        detail: {
          platformName: formData.platformName.trim(),
          platformIconDataUrl: iconDataUrl
        }
      }));
    } catch (err) {
      const errMsg = err.response?.data?.details || err.response?.data?.error || err.message || 'Unknown error';
      setError('Failed to save platform settings: ' + errMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setFormData({ ...settings });
    setSelectedFile(null);
    setRemoveIcon(false);
    setPreviewUrl(settings.platformIconDataUrl || null);
    setError(null);
    setSuccessMessage(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const dismissError = () => setError(null);

  const handleUserIdPrefixSave = async () => {
    try {
      setUserIdPrefixSaving(true);
      setUserIdPrefixError(null);
      setUserIdPrefixSuccess(null);

      const trimmed = userIdPrefixInput.trim().toUpperCase();
      if (trimmed && !/^[A-Z]{3}$/.test(trimmed)) {
        setUserIdPrefixError('Account ID prefix must be exactly 3 uppercase letters (A-Z)');
        setUserIdPrefixSaving(false);
        return;
      }

      const response = await adminSettingsAPI.updateUserIdPrefix(trimmed || null);
      const data = response.data.data || response.data;
      const newPrefix = data.userIdPrefix || '';
      setUserIdPrefix(newPrefix);
      setUserIdPrefixInput(newPrefix);
      setUserIdPrefixSuccess(data.message || 'Account ID prefix updated successfully.');
      setTimeout(() => setUserIdPrefixSuccess(null), 3000);
    } catch (err) {
      const errMsg = err.response?.data?.errors?.[0]?.msg || err.response?.data?.message || err.message || 'Unknown error';
      setUserIdPrefixError('Failed to save account ID prefix: ' + errMsg);
    } finally {
      setUserIdPrefixSaving(false);
    }
  };

  const handleUserIdPrefixClear = () => {
    setUserIdPrefixInput('');
  };

  const canManage = adminHasPermission('settings.maintenance') || adminHasPermission('system.super');

  return (
    <div className="psm-container">
      <div className="psm-header-section">
        <div className="psm-header-content">
          <h2 className="psm-title">
            <i className="fas fa-paint-brush"></i>
            Platform Settings
          </h2>
          <p className="psm-description">
            Manage platform branding, contact information, and social links displayed to end users
          </p>
        </div>
        <div className="psm-header-actions">
          <button className="psm-btn-secondary" onClick={fetchSettings} disabled={loading}>
            <i className="fas fa-sync-alt"></i>
            Refresh
          </button>
        </div>
      </div>

      {successMessage && (
        <div className="psm-success-notice">
          <i className="fas fa-check-circle"></i>
          <p>{successMessage}</p>
        </div>
      )}

      {error && (
        <div className="psm-error-notice">
          <i className="fas fa-exclamation-circle"></i>
          <p>{error}</p>
          <button className="close-btn" onClick={dismissError}>
            <i className="fas fa-times"></i>
          </button>
        </div>
      )}

      {loading ? (
        <div className="psm-loading-container">
          <div className="psm-loading-spinner"></div>
          <p className="psm-loading-text">Loading platform settings...</p>
        </div>
      ) : (
        <div className="psm-content">
          <div className="psm-form-section">
            <div className="psm-section-header">
              <h3 className="psm-section-title">
                <i className="fas fa-building"></i>
                Branding
              </h3>
            </div>
            <div className="psm-form-grid">
              <div className="psm-form-group full-width">
                <label className="psm-form-label" htmlFor="platformName">
                  Platform Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="platformName"
                  name="platformName"
                  className="psm-form-input"
                  value={formData.platformName}
                  onChange={handleInputChange}
                  placeholder="e.g., StandAlone Exchange"
                  disabled={!canManage}
                />
              </div>
              <div className="psm-form-group full-width">
                <label className="psm-form-label">
                  Platform Icon <span className="psm-dims-hint">(max 256x256)</span>
                </label>
                <div className="psm-icon-upload-area">
                  <div className="psm-icon-preview-box">
                    {previewUrl ? (
                      <img
                        src={previewUrl}
                        alt="Platform Icon"
                        className="psm-icon-preview-img"
                      />
                    ) : (
                      <div className="psm-icon-placeholder">
                        <i className="fas fa-image"></i>
                        <span>No icon</span>
                      </div>
                    )}
                  </div>
                  <div className="psm-icon-actions">
                    <label className={`psm-file-upload-btn ${!canManage ? 'disabled' : ''}`}>
                      <i className="fas fa-upload"></i>
                      {previewUrl ? 'Change Icon' : 'Upload Icon'}
                      <input
                        type="file"
                        ref={fileInputRef}
                        accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
                        onChange={handleFileSelect}
                        disabled={!canManage}
                        style={{ display: 'none' }}
                      />
                    </label>
                    {previewUrl && canManage && (
                      <button
                        className="psm-file-remove-btn"
                        onClick={handleRemoveIcon}
                        type="button"
                      >
                        <i className="fas fa-trash-alt"></i>
                        Remove
                      </button>
                    )}
                  </div>
                </div>
                <span className="psm-tip-badge">
                  <i className="fas fa-info-circle"></i>
                  PNG, JPEG, WebP, GIF, or SVG. Max 256x256px.
                </span>
              </div>
              <div className="psm-form-group full-width">
                <label className="psm-form-label" htmlFor="platformDescription">
                  Platform Description
                </label>
                <textarea
                  id="platformDescription"
                  name="platformDescription"
                  className="psm-form-textarea"
                  value={formData.platformDescription}
                  onChange={handleInputChange}
                  placeholder="Describe your platform..."
                  rows={3}
                  disabled={!canManage}
                />
              </div>
              <div className="psm-form-group">
                <label className="psm-form-label" htmlFor="orderIdPrefix">
                  Order ID Prefix
                </label>
                <input
                  type="text"
                  id="orderIdPrefix"
                  name="orderIdPrefix"
                  className="psm-form-input"
                  value={formData.orderIdPrefix}
                  onChange={handleInputChange}
                  placeholder="e.g., ORD"
                  disabled={!canManage}
                />
                <span className="psm-tip-badge">
                  <i className="fas fa-info-circle"></i>
                  Optional prefix prepended to all order IDs (e.g., &quot;ORD&quot; results in &quot;ORD-1000001&quot;)
                </span>
              </div>
            </div>
          </div>

          {userIdPrefixLoading ? (
            <div className="psm-form-section">
              <div className="psm-section-header">
                <h3 className="psm-section-title">
                  <i className="fas fa-id-card"></i>
                  Account ID Prefix
                </h3>
              </div>
              <div className="psm-form-grid">
                <div className="psm-loading-container">
                  <div className="psm-loading-spinner"></div>
                </div>
              </div>
            </div>
          ) : (
            <div className="psm-form-section">
              <div className="psm-section-header">
                <h3 className="psm-section-title">
                  <i className="fas fa-id-card"></i>
                  Account ID Prefix
                </h3>
              </div>
              <div className="psm-form-grid">
                {userIdPrefixSuccess && (
                  <div className="psm-form-group full-width">
                    <div className="psm-success-notice">
                      <i className="fas fa-check-circle"></i>
                      <p>{userIdPrefixSuccess}</p>
                    </div>
                  </div>
                )}
                {userIdPrefixError && (
                  <div className="psm-form-group full-width">
                    <div className="psm-error-notice">
                      <i className="fas fa-exclamation-circle"></i>
                      <p>{userIdPrefixError}</p>
                      <button className="close-btn" onClick={() => setUserIdPrefixError(null)}>
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  </div>
                )}
                <div className="psm-form-group">
                  <label className="psm-form-label" htmlFor="accountIdPrefix">
                    Account ID Prefix
                  </label>
                  <input
                    type="text"
                    id="accountIdPrefix"
                    name="accountIdPrefix"
                    className="psm-form-input"
                    value={userIdPrefixInput}
                    onChange={(e) => setUserIdPrefixInput(e.target.value.toUpperCase().slice(0, 3))}
                    placeholder="e.g., ABC"
                    maxLength={3}
                    disabled={!canManage}
                  />
                  <span className="psm-tip-badge">
                    <i className="fas fa-info-circle"></i>
                    Exactly 3 uppercase letters (A-Z). User account IDs will appear as "ABC-100001". Leave empty for plain numeric IDs.
                  </span>
                </div>
                <div className="psm-form-group">
                  <label className="psm-form-label">
                    Preview
                  </label>
                  <div className="psm-form-input" style={{ background: 'var(--admin-bg-secondary, #f8fafc)', display: 'flex', alignItems: 'center' }}>
                    <code style={{ fontSize: '0.875rem', color: 'var(--admin-text-primary, #1e293b)' }}>
                      {userIdPrefixInput ? `${userIdPrefixInput}-100001` : '100001'}
                    </code>
                  </div>
                </div>
                {canManage && (
                  <div className="psm-form-group full-width" style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                    <button
                      className="psm-btn-secondary"
                      onClick={handleUserIdPrefixClear}
                      disabled={userIdPrefixSaving || !userIdPrefixInput}
                    >
                      <i className="fas fa-undo"></i>
                      Clear
                    </button>
                    <button
                      className="psm-btn-primary"
                      onClick={handleUserIdPrefixSave}
                      disabled={userIdPrefixSaving}
                    >
                      {userIdPrefixSaving ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i>
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save"></i>
                          Save Prefix
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="psm-form-section">
            <div className="psm-section-header">
              <h3 className="psm-section-title">
                <i className="fas fa-envelope"></i>
                Contact & Legal
              </h3>
            </div>
            <div className="psm-form-grid">
              <div className="psm-form-group">
                <label className="psm-form-label" htmlFor="supportEmail">
                  Support Email
                </label>
                <input
                  type="email"
                  id="supportEmail"
                  name="supportEmail"
                  className="psm-form-input"
                  value={formData.supportEmail}
                  onChange={handleInputChange}
                  placeholder="support@example.com"
                  disabled={!canManage}
                />
              </div>
              <div className="psm-form-group">
                <label className="psm-form-label" htmlFor="supportUrl">
                  Support URL
                </label>
                <input
                  type="text"
                  id="supportUrl"
                  name="supportUrl"
                  className="psm-form-input"
                  value={formData.supportUrl}
                  onChange={handleInputChange}
                  placeholder="https://example.com/support"
                  disabled={!canManage}
                />
              </div>
              <div className="psm-form-group">
                <label className="psm-form-label" htmlFor="termsUrl">
                  Terms of Service URL
                </label>
                <input
                  type="text"
                  id="termsUrl"
                  name="termsUrl"
                  className="psm-form-input"
                  value={formData.termsUrl}
                  onChange={handleInputChange}
                  placeholder="https://example.com/terms"
                  disabled={!canManage}
                />
              </div>
              <div className="psm-form-group">
                <label className="psm-form-label" htmlFor="privacyUrl">
                  Privacy Policy URL
                </label>
                <input
                  type="text"
                  id="privacyUrl"
                  name="privacyUrl"
                  className="psm-form-input"
                  value={formData.privacyUrl}
                  onChange={handleInputChange}
                  placeholder="https://example.com/privacy"
                  disabled={!canManage}
                />
              </div>
            </div>
          </div>

          <div className="psm-form-section">
            <div className="psm-section-header">
              <h3 className="psm-section-title">
                <i className="fas fa-share-alt"></i>
                Social Links
              </h3>
            </div>
            <div className="psm-form-grid">
              <div className="psm-form-group">
                <label className="psm-form-label" htmlFor="twitter">
                  <i className="fab fa-twitter"></i> Twitter
                </label>
                <input
                  type="text"
                  id="twitter"
                  name="twitter"
                  className="psm-form-input"
                  value={formData.socialLinks.twitter}
                  onChange={handleSocialLinkChange}
                  placeholder="https://twitter.com/..."
                  disabled={!canManage}
                />
              </div>
              <div className="psm-form-group">
                <label className="psm-form-label" htmlFor="discord">
                  <i className="fab fa-discord"></i> Discord
                </label>
                <input
                  type="text"
                  id="discord"
                  name="discord"
                  className="psm-form-input"
                  value={formData.socialLinks.discord}
                  onChange={handleSocialLinkChange}
                  placeholder="https://discord.gg/..."
                  disabled={!canManage}
                />
              </div>
              <div className="psm-form-group">
                <label className="psm-form-label" htmlFor="telegram">
                  <i className="fab fa-telegram"></i> Telegram
                </label>
                <input
                  type="text"
                  id="telegram"
                  name="telegram"
                  className="psm-form-input"
                  value={formData.socialLinks.telegram}
                  onChange={handleSocialLinkChange}
                  placeholder="https://t.me/..."
                  disabled={!canManage}
                />
              </div>
              <div className="psm-form-group">
                <label className="psm-form-label" htmlFor="medium">
                  <i className="fab fa-medium"></i> Medium
                </label>
                <input
                  type="text"
                  id="medium"
                  name="medium"
                  className="psm-form-input"
                  value={formData.socialLinks.medium}
                  onChange={handleSocialLinkChange}
                  placeholder="https://medium.com/..."
                  disabled={!canManage}
                />
              </div>
              <div className="psm-form-group">
                <label className="psm-form-label" htmlFor="github">
                  <i className="fab fa-github"></i> GitHub
                </label>
                <input
                  type="text"
                  id="github"
                  name="github"
                  className="psm-form-input"
                  value={formData.socialLinks.github}
                  onChange={handleSocialLinkChange}
                  placeholder="https://github.com/..."
                  disabled={!canManage}
                />
              </div>
            </div>
          </div>

          {canManage && (
            <div className="psm-form-actions">
              <button
                className="psm-btn-secondary"
                onClick={handleReset}
                disabled={saving}
              >
                <i className="fas fa-undo"></i>
                Reset
              </button>
              <button
                className="psm-btn-primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save"></i>
                    Save Settings
                  </>
                )}
              </button>
            </div>
          )}

          {!canManage && (
            <div className="psm-permission-notice">
              <i className="fas fa-lock"></i>
              You need super admin or settings maintenance permissions to modify platform settings.
            </div>
          )}

          <div className="psm-info-section">
            <h4 className="psm-info-title">
              <i className="fas fa-info-circle"></i>
              Where These Settings Appear
            </h4>
            <ul className="psm-info-list">
              <li><strong>Platform Name & Icon:</strong> Displayed in the trading client header, login page, and email communications.</li>
              <li><strong>Description:</strong> Shown on landing pages and SEO meta tags.</li>
              <li><strong>Support Email/URL:</strong> Used in error messages, maintenance mode notices, and contact sections.</li>
              <li><strong>Terms & Privacy URLs:</strong> Linked in the registration form, footer, and legal sections.</li>
              <li><strong>Order ID Prefix:</strong> Prepended to all user-generated order IDs. Leave empty for plain numeric IDs.</li>
              <li><strong>Account ID Prefix:</strong> Prepended to all newly created user account IDs (e.g., "ABC-100001"). Changes only affect future accounts.</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlatformSettingsManager;
